import { supabase } from '@/integrations/supabase/client';
import i18n from '@/i18n/config';
import { getStatusConfig } from '@/lib/status-utils';

interface StatusChangeParams {
  carId: string;
  status: string;
  changedBy: string;
  reason: string;
  tenantId: string;
  metadata?: Record<string, any>;
}

interface EventParams {
  carId: string;
  eventType: string;
  triggeredBy: string;
  lineId: string;
  tenantId: string;
  data?: Record<string, any>;
}

export const logStatusChange = async ({
  carId,
  status,
  changedBy,
  reason,
  tenantId,
  metadata = {},
}: StatusChangeParams) => {
  const { error } = await ((supabase as any)
    .schema('mod_styling'))
    .from('car_status_history')
    .insert({
      tenant_id: tenantId,
      car_id: carId,
      status,
      changed_by: changedBy,
      reason,
      metadata,
    });

  if (error) {
    console.error('Failed to log status change:', error);
  }
};

export const logCarEvent = async ({
  carId,
  eventType,
  triggeredBy,
  lineId,
  tenantId,
  data = {},
}: EventParams) => {
  const { error } = await ((supabase as any)
    .schema('mod_styling'))
    .from('car_events')
    .insert({
      tenant_id: tenantId,
      car_id: carId,
      event_type: eventType,
      triggered_by: triggeredBy,
      line_id: lineId,
      data,
    });

  if (error) {
    console.error('Failed to log car event:', error);
  }
};

export interface TimelineItem {
  id: string;
  type: 'status' | 'event' | 'step';
  timestamp: string;
  title: string;
  description?: string;
  user?: string;
  metadata?: Record<string, any>;
}

interface StepInfo {
  id: string;
  title: string;
  step_number: number;
}

export const fetchCarTimeline = async (carId: string): Promise<TimelineItem[]> => {
  const timeline: TimelineItem[] = [];
  const profileMap = new Map<string, string>();
  const stationMap = new Map<string, string>();

  // Fetch car creation date for origin event
  const { data: carData } = await ((supabase as any)
    .schema('mod_styling'))
    .from('cars')
    .select('created_at, sap_date')
    .eq('id', carId)
    .single();

  // Fetch status history
  const { data: statusHistory } = await ((supabase as any)
    .schema('mod_styling'))
    .from('car_status_history')
    .select('id, status, changed_at, reason, metadata, changed_by')
    .eq('car_id', carId)
    .order('changed_at', { ascending: true });

  // Fetch profiles for status history
  const statusUserIds = [...new Set(statusHistory?.map(s => s.changed_by) || [])];
  if (statusUserIds.length > 0) {
    const { data: statusProfiles } = await ((supabase as any)
      .schema('core'))
      .from('profiles')
      .select('id, full_name')
      .in('id', statusUserIds);

    statusProfiles?.forEach(p => profileMap.set(p.id, p.full_name));
  }

  // Fetch all station IDs from status history metadata
  const stationIds = new Set<string>();
  statusHistory?.forEach(item => {
    if (item.metadata && typeof item.metadata === 'object' && 'station_id' in item.metadata) {
      stationIds.add((item.metadata as any).station_id);
    }
  });

  // Fetch station names
  if (stationIds.size > 0) {
    const { data: stations } = await ((supabase as any)
      .schema('mod_styling'))
      .from('stations')
      .select('id, station_number, name')
      .in('id', Array.from(stationIds));

    stations?.forEach(s => {
      const displayName = s.name || `Station ${s.station_number}`;
      stationMap.set(s.id, displayName);
    });
  }

  statusHistory?.forEach((item) => {
    // Clean up metadata: replace station_id with station_name, remove previous_operator_id
    let cleanMetadata: Record<string, any> | undefined;
    if (item.metadata && typeof item.metadata === 'object') {
      const meta = { ...(item.metadata as any) };
      delete meta.previous_operator_id;

      if (meta.station_id && stationMap.has(meta.station_id)) {
        meta.station = stationMap.get(meta.station_id);
        delete meta.station_id;
      }

      if (Object.keys(meta).length > 0) {
        cleanMetadata = meta;
      }
    }

    timeline.push({
      id: `status-${item.id}`,
      type: 'status',
      timestamp: item.changed_at!,
      title: i18n.t('historyTimeline.statusTitle', { status: formatStatus(item.status) }),
      description: item.reason ? formatReason(item.reason) : undefined,
      user: profileMap.get(item.changed_by) || undefined,
      metadata: cleanMetadata,
    });
  });

  // Fetch events
  const { data: events } = await ((supabase as any)
    .schema('mod_styling'))
    .from('car_events')
    .select('id, event_type, occurred_at, data, triggered_by')
    .eq('car_id', carId)
    .order('occurred_at', { ascending: true });

  // Fetch profiles for events
  const eventUserIds = [...new Set(events?.map(e => e.triggered_by) || [])];
  if (eventUserIds.length > 0) {
    const { data: eventProfiles } = await ((supabase as any)
      .schema('core'))
      .from('profiles')
      .select('id, full_name')
      .in('id', eventUserIds);

    eventProfiles?.forEach(p => profileMap.set(p.id, p.full_name));
  }

  // Also fetch station IDs from events
  events?.forEach(item => {
    if (item.data && typeof item.data === 'object' && 'station_id' in item.data) {
      stationIds.add((item.data as any).station_id);
    }
  });

  // Fetch additional station names if needed
  if (stationIds.size > stationMap.size) {
    const { data: additionalStations } = await ((supabase as any)
      .schema('mod_styling'))
      .from('stations')
      .select('id, station_number, name')
      .in('id', Array.from(stationIds));

    additionalStations?.forEach(s => {
      if (!stationMap.has(s.id)) {
        const displayName = s.name || `Station ${s.station_number}`;
        stationMap.set(s.id, displayName);
      }
    });
  }

  events?.forEach((item) => {
    // Clean up event metadata
    let cleanMetadata: Record<string, any> | undefined;
    if (item.data && typeof item.data === 'object') {
      const meta = { ...(item.data as any) };
      delete meta.previous_operator_id;

      if (meta.station_id && stationMap.has(meta.station_id)) {
        meta.station = stationMap.get(meta.station_id);
        delete meta.station_id;
      }

      if (Object.keys(meta).length > 0) {
        cleanMetadata = meta;
      }
    }

    timeline.push({
      id: `event-${item.id}`,
      type: 'event',
      timestamp: item.occurred_at!,
      title: formatEventType(item.event_type),
      user: profileMap.get(item.triggered_by) || undefined,
      metadata: cleanMetadata,
    });
  });

  // Fetch step completions
  const { data: completions } = await ((supabase as any)
    .schema('mod_styling'))
    .from('step_completions')
    .select('id, completed_at, step_id, operator_id')
    .eq('car_id', carId)
    .order('completed_at', { ascending: true });

  // Fetch step details
  const stepIds = [...new Set(completions?.map(c => c.step_id) || [])];
  const stepMap = new Map<string, StepInfo>();
  
  if (stepIds.length > 0) {
    const { data: steps } = await ((supabase as any)
      .schema('mod_styling'))
      .from('instruction_steps')
      .select('id, title, step_number')
      .in('id', stepIds);
    
    steps?.forEach(s => stepMap.set(s.id, s as StepInfo));
  }

  // Fetch profiles for completions
  const completionUserIds = [...new Set(completions?.map(c => c.operator_id) || [])];
  if (completionUserIds.length > 0) {
    const { data: completionProfiles } = await ((supabase as any)
      .schema('core'))
      .from('profiles')
      .select('id, full_name')
      .in('id', completionUserIds);
    
    completionProfiles?.forEach(p => profileMap.set(p.id, p.full_name));
  }

  completions?.forEach((item) => {
    const step = stepMap.get(item.step_id);
    timeline.push({
      id: `step-${item.id}`,
      type: 'step',
      timestamp: item.completed_at!,
      title: step
        ? i18n.t('historyTimeline.stepTitle', { number: step.step_number, title: step.title })
        : i18n.t('historyTimeline.stepCompletedFallback'),
      user: profileMap.get(item.operator_id) || undefined,
    });
  });

  // Add origin event at the beginning (will be at the end after reverse sort)
  if (carData) {
    const originDate = carData.sap_date || carData.created_at;
    if (originDate) {
      timeline.push({
        id: 'origin',
        type: 'event',
        timestamp: originDate,
        title: i18n.t('historyTimeline.originTitle'),
        description: i18n.t('historyTimeline.originDescription'),
      });
    }
  }

  // Sort by timestamp (newest first)
  return timeline.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
};

const formatStatus = (status: string): string => {
  return i18n.t(getStatusConfig(status).labelKey);
};

const formatReason = (reason: string): string => {
  const map: Record<string, string> = {
    car_claimed: 'historyTimeline.reasons.car_claimed',
    car_taken_over: 'historyTimeline.reasons.car_taken_over',
    all_steps_completed: 'historyTimeline.reasons.all_steps_completed',
    manual_override: 'historyTimeline.reasons.manual_override',
  };
  const key = map[reason];
  if (key) return i18n.t(key);
  return reason.replace(/_/g, ' ');
};

const formatEventType = (type: string): string => {
  const map: Record<string, string> = {
    car_claimed: 'historyTimeline.events.car_claimed',
    car_released: 'historyTimeline.events.car_released',
    car_taken_over: 'historyTimeline.events.car_taken_over',
    site_changed: 'historyTimeline.events.site_changed',
    manual_edit: 'historyTimeline.events.manual_edit',
  };
  const key = map[type];
  if (key) return i18n.t(key);
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};
